import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// react-bootstrap
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';

// project-imports
import useConfig from 'hooks/useConfig';
import { ThemeMode } from 'config';
import { setResolvedTheme, getResolvedTheme } from 'components/setResolvedTheme';
import useAuth from '../../auth/useAuth';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import SimpleBarScroll from 'components/third-party/SimpleBar';
import './header.css';

import { getPusher } from 'utils/pusher';

import { useGetNotifications, useReadAllNotifications, useReadNotification } from '../../APIs/Notification/Notification';
import { notify } from '@common/NotificationService';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  merchant_admin: 'Merchant Admin',
  manager: 'Manager',
  staff: 'Staff'
};

const titleCase = (s) => String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Up to two initials from a name: "Julia Munroe" → "JM", "Spice" → "SP".
const initialsOf = (name = '') => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

// The icon beside each notification, by what it is about.
const notificationIcon = (type = '') => {
  const t = String(type).toLowerCase();
  if (t.startsWith('booking')) return 'ph-calendar-check';
  if (t.startsWith('giftcard')) return 'ph-gift';
  return 'ph-bell';
};

export default function Header() {
  const { onChangeMode, mode } = useConfig();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { notifications: apiNotifications } = useGetNotifications();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const { readAll } = useReadAllNotifications();
  const { readOne } = useReadNotification();
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    setResolvedTheme(mode);
  }, [mode]);

  useEffect(() => {
    if (!apiNotifications.length) return;

    const unread = apiNotifications.filter((n) => !n.is_read);

    setNotifications(unread);
    setUnreadCount(unread.length);
  }, [apiNotifications.length]);

  useEffect(() => {
    if (!user?.user?.id) return;

    const pusher = getPusher();
    const channelName = `user.${user.user.id}`;
    const channel = pusher.subscribe(channelName);

    const handleEvent = (payload) => {
      setNotifications((prev) => {
        // prevent duplicates
        if (prev.some((n) => n.id === payload.id)) return prev;
        return [payload, ...prev];
      });

      setUnreadCount((prev) => prev + 1);
    };

    channel.bind('booking.notification', handleEvent);

    return () => {
      channel.unbind('booking.notification', handleEvent);
      pusher.unsubscribe(channelName);
    };
  }, [user?.user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRead = async (id) => {
    await readOne(id);

    setNotifications((prev) => prev.filter((n) => n.id !== id));

    setUnreadCount((prev) => Math.max(prev - 1, 0));
  };

  const handleNotificationClick = async (notification) => {
    try {
      await readOne(notification.id);
    } catch (e) {
      // ignore read failure for UX
    }

    // remove from unread dropdown
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    setUnreadCount((prev) => Math.max(prev - 1, 0));
    setShowNotifications(false);

    // redirect for booking-related notifications
    if (notification.type?.toLowerCase().startsWith('booking')) {
      navigate('/booking-management', {
        state: {
          bookingId: notification.booking_id,
          openView: true
        }
      });
    } else if (notification.type?.toLowerCase().startsWith('giftcard')) {
      navigate('./gift_card');
    }
  };

  const handleReadAll = async (e) => {
    e.preventDefault();

    try {
      const res = await readAll();

      if (res?.success) {
        setNotifications([]);
        setUnreadCount(0);
      } else {
        notify.error(res?.message || 'Failed to mark notifications as read', '');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Something went wrong while marking notifications as read';

      notify.error(errorMessage, '');
    }
  };

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  const userName = user?.user?.name || 'Guest User';
  const roleLabel = ROLE_LABELS[user?.user?.user_type] || (user?.user?.user_type ? titleCase(user.user.user_type) : '');
  // Two themes only. A saved "Default" (follow the device) shows as whichever
  // of the two it currently resolves to, and the button switches to the other.
  const activeTheme = getResolvedTheme(mode);
  const themeIcon = activeTheme === ThemeMode.DARK ? 'ph-moon' : 'ph-sun';

  return (
    <header className="pc-header yz-hdr">
      <div className="header-wrapper">
        {/* LEFT — the sidebar toggle, on phones only. Desktop shrink/expand
            lives on the sidebar's own button (SidebarUserCard). */}
        <div className="me-auto pc-mob-drp">
          <Nav className="list-unstyled">
            <Nav.Item className="pc-h-item pc-sidebar-popup">
              <Nav.Link as={Link} to="#" className="pc-head-link ms-0" id="mobile-collapse" onClick={() => handlerDrawerOpen(!drawerOpen)}>
                <i className="ph ph-list" />
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {/* RIGHT — notifications, theme, profile */}
        <div className="ms-auto yz-hdr-right">
          {/* NOTIFICATIONS */}
          <Dropdown align="end" show={showNotifications} onToggle={(isOpen) => setShowNotifications(isOpen)}>
            <Dropdown.Toggle as="button" type="button" className="yz-hdr-btn arrow-none" id="notification-dropdown" aria-label="Notifications">
              <i className="ph ph-bell" />
              {unreadCount > 0 && <span className="yz-hdr-count">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </Dropdown.Toggle>

            <Dropdown.Menu className="yz-hdr-menu yz-hdr-notifs">
              <div className="yz-hdr-notifs-head">
                <span className="yz-hdr-notifs-title">Notifications</span>
                <div className="yz-hdr-notifs-links">
                  {notifications.length > 0 && (
                    <Link to="#" onClick={handleReadAll}>
                      Mark all read
                    </Link>
                  )}
                  <Link to="/notifications" onClick={() => setShowNotifications(false)}>
                    View all
                  </Link>
                </div>
              </div>

              {notifications.length === 0 ? (
                <div className="yz-hdr-notifs-empty">
                  <span className="yz-hdr-notifs-empty-icon">
                    <i className="ph ph-check-circle" />
                  </span>
                  You&apos;re all caught up
                </div>
              ) : (
                <SimpleBarScroll style={{ maxHeight: 'calc(100vh - 215px)' }}>
                  <div className="yz-hdr-notifs-list">
                    {notifications.map((notification) => (
                      <button
                        key={notification?.id}
                        type="button"
                        className="yz-hdr-notif"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <span className="yz-hdr-notif-icon">
                          <i className={`ph ${notificationIcon(notification?.type)}`} />
                        </span>
                        <span className="yz-hdr-notif-body">
                          <span className="yz-hdr-notif-top">
                            <span className="yz-hdr-notif-title">{notification?.title}</span>
                            <span className="yz-hdr-notif-time">{notification?.created_at}</span>
                          </span>
                          <span className="yz-hdr-notif-msg">{notification?.message}</span>
                        </span>
                        <span className="yz-hdr-notif-dot" aria-label="Unread" />
                      </button>
                    ))}
                  </div>
                </SimpleBarScroll>
              )}
            </Dropdown.Menu>
          </Dropdown>

          {/* THEME — one click switches between Light and Dark. */}
          <button
            type="button"
            className="yz-hdr-btn"
            onClick={() => onChangeMode(activeTheme === ThemeMode.DARK ? ThemeMode.LIGHT : ThemeMode.DARK)}
            aria-label={`Switch to ${activeTheme === ThemeMode.DARK ? 'light' : 'dark'} mode`}
            title={`Switch to ${activeTheme === ThemeMode.DARK ? 'light' : 'dark'} mode`}
          >
            {/* Keyed by the theme so the new icon spins in on a change. */}
            <i key={themeIcon} className={`ph ${themeIcon} yz-hdr-theme-icon`} />
          </button>

          {/* PROFILE */}
          <Dropdown align="end">
            <Dropdown.Toggle as="button" type="button" className="yz-hdr-profile arrow-none" id="user-profile-dropdown">
              <span className="yz-hdr-avatar">{initialsOf(userName)}</span>
              <span className="yz-hdr-who">
                <span className="yz-hdr-name">{userName}</span>
                {roleLabel && <span className="yz-hdr-role">{roleLabel}</span>}
              </span>
              <i className="ph ph-caret-down yz-hdr-caret" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="yz-hdr-menu yz-hdr-user">
              <div className="yz-hdr-user-card">
                <span className="yz-hdr-avatar yz-hdr-avatar--lg">{initialsOf(userName)}</span>
                <div className="yz-hdr-user-text">
                  <div className="yz-hdr-user-name">{userName}</div>
                  <div className="yz-hdr-user-email">{user?.user?.email || 'no-email'}</div>
                </div>
              </div>

              <Dropdown.Item as={Link} to="/settings">
                <i className="ph ph-gear" />
                Settings
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item as="button" type="button" className="yz-hdr-logout" onClick={handleLogout}>
                <i className="ph ph-sign-out" />
                Log out
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </header>
  );
}
